import DigiobcanApi, {
  type NewsSourcePath,
} from '../../../../api/DigiobcanApi';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import FormTextField from '../../../../components/form/FormTextField';
import { getNewsCategories } from '../../../../features/queries/getNewsCategories';
import { getNewsFeeds } from '../../../../features/queries/getNewsFeeds';
import { getNewsSources } from '../../../../features/queries/getNewsSources';
import { entityId } from '../../../../lib/ids';
import { useM, useQ } from '../../../../lib/query';
import { useLayout } from '../../../../providers/LayoutProvider';
import { useLogger } from '../../../../providers/LoggerProvider';
import { useResponsive } from '../../../../providers/ResponsiveProvider';
import {
  __,
  declension,
  required,
  toastError,
  toastSuccess,
} from '../../../../utils';
import {
  encodePath,
  formatSourcePath,
  newsCountForPattern,
} from '../newsSources';
import AddNewsCategorySourcesDialog from './AddNewsCategorySourcesDialog';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Card,
  DataTable,
  EmptyState,
  Stack,
  Typography,
  type Column,
} from '@presov.sk/presov-ui-react';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { Form } from 'react-final-form';
import { useParams } from 'react-router';

/** Šírka ľavého stĺpca s údajmi - ako v detaile kategórie podnetov. */
const FORM_COLUMN_WIDTH = 420;

type SourceRow = { key: string; path: NewsSourcePath; count: number };

/**
 * Detail kategórie noviniek: naľavo jej údaje, napravo zdroje, ktoré do nej
 * patria. Podľa vzoru detailu kategórie podnetov - tam sú napravo ľudia,
 * tu zdroje, a obe sa pridávajú a odoberajú rovnako (výber riadkov a
 * „Odobrať", dialóg „Pridať").
 *
 * Samostatný dotaz na jednu kategóriu API nemá a netreba ho: kategórií je
 * sedem a zoznam už býva v cache zo stránky, z ktorej sa sem prišlo.
 */
const NewsCategoryAdminPage: React.FC = () => {
  const { categoryId: categoryIdParam } = useParams() as {
    categoryId: string;
  };
  const categoryId = entityId(categoryIdParam, 'category');
  const queryClient = useQueryClient();
  const { logger } = useLogger();
  const { setPageTitle } = useLayout();
  const { isLg, isMd } = useResponsive();

  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);
  const [showAddDialog, setShowAddDialog] = React.useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = React.useState(false);

  const { data: categories = [], isLoading: isLoadingCategories } = useQ(
    getNewsCategories,
    { view: 'flat' },
  );
  const category = categories.find((c) => c.category_id === categoryId);

  const { data: newsSources = [], isLoading: isLoadingSources } =
    useQ(getNewsSources);

  React.useEffect(() => {
    if (category?.name) setPageTitle(category.name);
  }, [category, setPageTitle]);

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: [getNewsCategories.toString()],
    });
    queryClient.invalidateQueries({ queryKey: [getNewsSources.toString()] });
    // Zaradenie mení aj stav zdrojov noviniek (Nezaradený → Čaká).
    queryClient.invalidateQueries({ queryKey: [getNewsFeeds.toString()] });
  };

  const updateCategory = useM(
    (values: { name: string; description?: string }) =>
      DigiobcanApi.updateNewsCategory(
        categoryId,
        {},
        { name: values.name, description: values.description || null },
      ),
    {
      onSuccess: () => {
        toastSuccess(__('Kategória bola aktualizovaná'));
        invalidate();
      },
      onError: (e, variables) => {
        logger.error('Error updating news category', {
          action: 'updateNewsCategory',
          error: e,
          data: { categoryId, ...variables },
        });
        toastError(__('Niečo sa pokazilo'));
      },
    },
  );

  const updateSources = useM(
    (paths: NewsSourcePath[]) =>
      DigiobcanApi.updateNewsCategorySources(
        categoryId,
        {},
        { sources: paths },
      ),
    {
      onSuccess: ({ news_count }) => {
        toastSuccess(
          `${__('Zdroje uložené, v kategórii je')} ${news_count} ${declension(
            news_count,
            [__('novinka'), __('novinky'), __('noviniek')],
          )}.`,
        );
        invalidate();
        setSelectedRows([]);
        setShowAddDialog(false);
        setShowRemoveDialog(false);
      },
      onError: (e, paths) => {
        logger.error('Error updating news category sources', {
          action: 'updateNewsCategorySources',
          error: e,
          data: { categoryId, sources: paths },
        });
        toastError(__('Zdroje sa nepodarilo uložiť.'));
      },
    },
  );

  const sources = React.useMemo(() => category?.sources ?? [], [category]);

  const rows: SourceRow[] = React.useMemo(
    () =>
      sources.map((path) => ({
        key: encodePath(path),
        path,
        count: newsCountForPattern(newsSources, path),
      })),
    [sources, newsSources],
  );

  const columns: Column<SourceRow>[] = React.useMemo(
    () => [
      {
        key: 'path',
        label: __('Zdroj'),
        width: '3fr',
        minWidth: '220px',
        strong: true,
        render: (row) => formatSourcePath(row.path),
      },
      {
        key: 'count',
        label: __('Noviniek'),
        width: '0.8fr',
        minWidth: '90px',
        // Vzor bez noviniek je buď starý názov (napr. `PKO`), alebo zdroj,
        // ktorý sa ešte nestiahol - nie je to chyba, ale oplatí sa to vidieť.
        render: (row) => (
          <Typography
            variant='body2'
            color={row.count ? undefined : 'text.secondary'}
          >
            {row.count.toLocaleString('sk')}
          </Typography>
        ),
      },
    ],
    [],
  );

  const initialValues = React.useMemo(
    () => ({
      name: category?.name ?? '',
      description: category?.description ?? '',
    }),
    [category],
  );

  if (!isLoadingCategories && !category) {
    return (
      <Box sx={{ padding: 1.5 }}>
        <EmptyState
          icon='search'
          title={__('Kategória sa nenašla')}
          description={__('Možno bola medzitým zmazaná.')}
        />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isLg
            ? `minmax(0, ${FORM_COLUMN_WIDTH}px) minmax(0, 1fr)`
            : 'minmax(0, 1fr)',
          gap: 1.5,
          padding: 1.5,
          alignItems: 'start',
        }}
      >
        <Card sx={{ padding: 2 }}>
          <Typography variant='subtitle2' sx={{ mb: 1.5 }}>
            {__('Údaje kategórie')}
          </Typography>
          <Form
            onSubmit={(values: { name: string; description?: string }) => {
              if (!navigator.onLine) {
                toastError(__('Internetové pripojenie zlyhalo.'));
                return;
              }
              updateCategory.mutate(values);
            }}
            // `key`: formulár sa napĺňa až po načítaní, bez neho by si
            // `react-final-form` podržal prázdne počiatočné hodnoty.
            key={category?.category_id ?? 'loading'}
            initialValues={initialValues}
          >
            {({ handleSubmit, pristine }) => (
              <form
                onSubmit={handleSubmit}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <FormTextField
                  required
                  label={__('Názov kategórie')}
                  name='name'
                  validate={required}
                  maxLength={120}
                />
                <FormTextField
                  label={__('Popis kategórie')}
                  name='description'
                  multiline
                  rows={3}
                  maxLength={500}
                />
                <Stack direction='row' justifyContent='flex-end' mt={1.5}>
                  <Button
                    type='submit'
                    variant='dark'
                    size='small'
                    disabled={pristine || updateCategory.isPending}
                  >
                    {__('Uložiť zmeny')}
                  </Button>
                </Stack>
              </form>
            )}
          </Form>
        </Card>

        <Card sx={{ padding: 2 }}>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            gap={1}
            sx={{ mb: 0.5 }}
          >
            <Typography variant='subtitle2'>{__('Zdroje')}</Typography>
            <Stack direction='row' gap={1} className='screen-only'>
              {selectedRows.length ? (
                <Button
                  size='small'
                  variant='danger'
                  onClick={() => setShowRemoveDialog(true)}
                >
                  {`${__('Odobrať')} (${selectedRows.length})`}
                </Button>
              ) : null}
              <Button
                size='small'
                variant='dark'
                onClick={() => setShowAddDialog(true)}
              >
                <AddIcon fontSize='small' sx={{ mr: 0.5 }} />
                {__('Pridať zdroj')}
              </Button>
            </Stack>
          </Stack>
          <Typography
            variant='caption'
            color='text.secondary'
            component='p'
            sx={{ mb: 1.5 }}
          >
            {__(
              'Novinka patrí do kategórie, keď jej zdroj začína niektorým z týchto zdrojov. Zmena sa na novinkách prejaví hneď a notifikácie pritom neodchádzajú.',
            )}
          </Typography>

          <DataTable
            variant={isMd ? 'table' : 'cards'}
            columns={columns}
            rows={rows}
            rowKey='key'
            selected={selectedRows}
            onSelectionChange={(next) => setSelectedRows(next.map(String))}
            selectAllLabel={__('Označiť všetky zdroje')}
            selectRowLabel={__('Označiť zdroj')}
            loading={isLoadingCategories || isLoadingSources}
            loadingRows={3}
            empty={
              <EmptyState
                compact
                icon='list'
                title={__('Kategória nemá žiadne zdroje')}
                description={__(
                  'Nepribúdajú do nej novinky a jej odberatelia nedostanú nič.',
                )}
                actions={
                  <Button
                    variant='outline'
                    size='small'
                    onClick={() => setShowAddDialog(true)}
                  >
                    {__('Pridať zdroj')}
                  </Button>
                }
              />
            }
          />
        </Card>
      </Box>

      <AddNewsCategorySourcesDialog
        open={showAddDialog}
        sources={newsSources}
        existing={sources}
        isLoading={updateSources.isPending}
        onClose={() => setShowAddDialog(false)}
        onAdd={(paths) => updateSources.mutate([...sources, ...paths])}
      />

      <ConfirmationDialog
        open={showRemoveDialog}
        title={__('Odobrať vybrané zdroje?')}
        description={`${__('Novinky z týchto zdrojov z kategórie hneď vypadnú:')} ${rows
          .filter((row) => selectedRows.includes(row.key))
          .map((row) => formatSourcePath(row.path))
          .join(', ')}`}
        confirmLabel={__('Odobrať')}
        isLoading={updateSources.isPending}
        onClose={() => setShowRemoveDialog(false)}
        onConfirm={() =>
          updateSources.mutate(
            sources.filter((path) => !selectedRows.includes(encodePath(path))),
          )
        }
        fullWidth
        maxWidth='xs'
      />
    </>
  );
};

export default NewsCategoryAdminPage;
